import React, {useCallback, useMemo} from 'react';
import {useAppSelector, useAppDispatch} from '@/shared/hooks/redux';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {AdminService} from '@/modules/admin/services/adminService';
import {setCurrentPatient} from '@/slices/patientSlice';
import {buildAdminOrigin} from '@/shared/utils/adminHost';
import {getBaseDomain} from '@/shared/utils/domain';
import {hideBanner} from '@/slices/bannerSlice';
import {decodeJwtToken} from '@/shared/utils/tokenUtils';

const Banner: React.FC = () => {
    const {user, setUser} = useAuth();
    const dispatch = useAppDispatch();
    const {visible, message} = useAppSelector((state) => state.banner);

    // Primary source of truth: the JWT's impersonate_id field.
    // Falls back to localStorage for legacy sessions that lack the field.
    const tokenImpersonateId = useMemo(() => {
        if (!user?.token) return null;
        const payload = decodeJwtToken(user.token);
        return payload?.impersonate_id ?? null;
    }, [user?.token]);

    const originalAdminDataString =
        sessionStorage.getItem('originalAdminData') || localStorage.getItem('originalAdminData');
    const originalAdminData = originalAdminDataString ? JSON.parse(originalAdminDataString) : null;
    const isPatientSwitching = originalAdminData?.switchUser === true;
    const isAdminImpersonating = !!tokenImpersonateId || (!!originalAdminDataString && !isPatientSwitching);
    const isImpersonating = isPatientSwitching || isAdminImpersonating;

    const handleExitImpersonation = useCallback(async () => {
        try {
            // Get original admin data from localStorage
            const originalAdminData =
                sessionStorage.getItem('originalAdminData') || localStorage.getItem('originalAdminData');
            if (!originalAdminData) {
                // If we don't have original admin data, just logout
                setUser(undefined as any);
                window.location.href = '/login';
                return;
            }

            const parsedAdminData = JSON.parse(originalAdminData);

            if (!parsedAdminData.userInfo || !parsedAdminData.token) {
                // Clean up bad data and logout
                localStorage.removeItem('originalAdminData');
                sessionStorage.removeItem('originalAdminData');
                setUser(undefined as any);
                window.location.href = '/login';
                return;
            }

            const isSessionOnly =
                Boolean(user?.sessionOnly) || sessionStorage.getItem('auth_session_only') === '1';

            // Restore from localStorage (session-only launch flow).
            const returnUser = {
                token: parsedAdminData.token,
                userInfo: {
                    ...parsedAdminData.userInfo,
                    // Clear impersonation flags
                    impersonate_id: undefined,
                    isImpersonating: false,
                },
                username: parsedAdminData.username,
                isLoggedIn: true,
                sessionOnly: false,
            };

            setUser(returnUser);

            // Update Redux current patient back to admin
            const adminPatientData = {
                id: parsedAdminData.userInfo.id,
                firstName: parsedAdminData.userInfo.firstName,
                lastName: parsedAdminData.userInfo.lastName,
                fullName:
                    parsedAdminData.userInfo.fullName ||
                    `${parsedAdminData.userInfo.firstName} ${parsedAdminData.userInfo.lastName}`,
                birthdate: parsedAdminData.userInfo.birthday,
            };
            dispatch(setCurrentPatient(adminPatientData));

            // Hide the banner
            dispatch(hideBanner());

            const storedReturnUrl =
                sessionStorage.getItem('impersonationReturnUrl') || localStorage.getItem('impersonationReturnUrl');
            const storedReturnOrigin =
                sessionStorage.getItem('impersonationReturnOrigin') ||
                localStorage.getItem('impersonationReturnOrigin');

            // Clean up stored admin data
            localStorage.removeItem('originalAdminData');
            sessionStorage.removeItem('originalAdminData');
            localStorage.removeItem('impersonationReturnUrl');
            sessionStorage.removeItem('impersonationReturnUrl');
            localStorage.removeItem('impersonationReturnOrigin');
            sessionStorage.removeItem('impersonationReturnOrigin');

            // Navigate back to admin dashboard (always on dashboard subdomain).
            const fallbackReturn = '/admin?orgConsole=1';
            const safePath =
                storedReturnUrl && storedReturnUrl.startsWith('/admin') ? storedReturnUrl : fallbackReturn;

            const resolveSafeReturnOrigin = () => {
                if (!storedReturnOrigin) return null;
                try {
                    const parsed = new URL(storedReturnOrigin);
                    const baseDomain = getBaseDomain(window.location.hostname);
                    if (!baseDomain) return null;
                    const host = parsed.hostname;
                    if (host === baseDomain || host.endsWith(`.${baseDomain}`)) {
                        return `${parsed.protocol}//${parsed.host}`;
                    }
                } catch {
                    return null;
                }
                return null;
            };

            const safeOrigin = resolveSafeReturnOrigin();
            const adminOrigin = buildAdminOrigin(window.location);

            // If we can safely return to a specific org subdomain, do it directly.
            if (safeOrigin && safeOrigin !== adminOrigin) {
                window.location.href = `${safeOrigin}${safePath}`;
                return;
            }

            if (isSessionOnly && parsedAdminData?.token && parsedAdminData?.userInfo?.organization_id) {
                try {
                    const switchToken = await AdminService.createOrgSwitchTokenWithToken(
                        Number(parsedAdminData.userInfo.organization_id),
                        parsedAdminData.token
                    );
                    await AdminService.exchangeOrgSwitchToken(switchToken.token);
                    window.location.href = `${adminOrigin}${safePath}`;
                    return;
                } catch {
                    // Fall back to default redirect below.
                }
            }

            window.location.href = `${adminOrigin}${safePath}`;
        } catch {
            alert('Failed to exit impersonation. Please try again.');
        }
    }, [dispatch, setUser, user?.sessionOnly]);

    // Show banner if Redux banner is visible OR if user is being impersonated
    const shouldShowBanner = visible || isImpersonating;

    if (!shouldShowBanner) return null;

    return (
        <div className="bg-mld-info/5 border border-mld-info/20 px-4 py-3 text-mld-info text-sm">
            <div className="flex items-center justify-center">
                {isImpersonating ? (
                    <p>
                        {isPatientSwitching ? (
                            <>
                                Viewing user{' '}
                                {user?.userInfo?.fullName ||
                                    `${user?.userInfo?.firstName} ${user?.userInfo?.lastName}` ||
                                    'Unknown User'}{' '}
                                <button
                                    onClick={handleExitImpersonation}
                                    className="text-mld-info underline cursor-pointer bg-transparent border-none font-medium hover:opacity-80">
                                    Switch Back
                                </button>
                            </>
                        ) : (
                            <>
                                Admin impersonating user{' '}
                                {user?.userInfo?.fullName ||
                                    `${user?.userInfo?.firstName} ${user?.userInfo?.lastName}` ||
                                    'Unknown User'}{' '}
                                <button
                                    onClick={handleExitImpersonation}
                                    className="text-mld-info underline cursor-pointer bg-transparent border-none font-medium hover:opacity-80">
                                    Exit Impersonation
                                </button>
                            </>
                        )}
                    </p>
                ) : (
                    <p>{message}</p>
                )}
            </div>
        </div>
    );
};

export default Banner;
